import { z } from 'zod';
import {
  ITEM_CATEGORIES,
  itemDefinitionIdSchema,
  RESERVATION_PURPOSES,
} from '../inventory/definitions';
import { loadoutSlotSchema } from '../inventory/loadout';
import { TRADE_BLOCK_REASONS } from '../inventory/tradability';
import { raritySchema } from '../rarity';
import { decimalStringSchema } from './hunt';

/**
 * Inventory wire contract - READ ONLY.
 *
 * There is deliberately no write contract in this file and no route through
 * which a client could name an item it wants: granting, transferring,
 * reserving and consuming are internal service operations
 * (`apps/api/src/inventory/service.ts`), reachable only from server-side
 * flows. The browser can look at the vault; it can never reach into it.
 */

export const INVENTORY_ROUTE = '/api/inventory' as const;

/**
 * Bumped when the response shape changes incompatibly.
 *
 * ## Versão 2 - a categoria `card` (gate V28H.C)
 *
 * `inventoryInstanceSchema.category` e `inventoryStackSchema.category` são
 * `z.enum(ITEM_CATEGORIES)`, e essa lista ganhou um valor. Um cliente da versão
 * 1 valida a resposta contra a enumeração antiga e RECUSA uma linha de carta -
 * não a ignora, recusa a página inteira. É uma mudança incompatível de formato,
 * e é exatamente para isto que este número existe.
 *
 * Nada mais aqui mudou: os mesmos campos, os mesmos tipos, a mesma paginação.
 * Uma categoria nova é o mínimo que obriga esta versão a mover-se, e a caça, o
 * PvP, as instantâneas, o equilíbrio, a aparência e o conteúdo das joias não
 * atravessam este contrato - por isso nenhuma dessas versões se move com ele.
 *
 * ## Versão 3 - os três espaços de carta (gate V28H.D)
 *
 * `inventoryInstanceSchema.equippedSlot` é `loadoutSlotSchema`, e `LOADOUT_SLOTS`
 * ganhou três valores. A razão é a mesma da versão 2 e a consequência também: um
 * cliente da versão 2 valida contra a enumeração antiga e RECUSA a página
 * inteira ao ver uma carta equipada.
 *
 * E o campo passa mesmo a levá-los: uma carta num espaço responde
 * `equipped: true` com `equippedSlot: 'card_slot_1'`. Deixá-lo a `null` seria
 * mentir sobre um item que a troca e o mercado já recusam por estar equipado.
 */
export const INVENTORY_CONTRACT_VERSION = 3;

/** Page size bounds for `GET /api/inventory`. */
export const INVENTORY_PAGE_SIZE_DEFAULT = 50;
export const INVENTORY_PAGE_SIZE_MAX = 100;

/**
 * Opaque pagination cursor. The client stores and returns it verbatim; its
 * internal shape is the server's business and may change at any time.
 */
export const inventoryCursorSchema = z.string().min(1).max(200);

export const inventoryQuerySchema = z
  .object({
    cursor: inventoryCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(INVENTORY_PAGE_SIZE_MAX).optional(),
  })
  .strict();
export type InventoryQuery = z.infer<typeof inventoryQuerySchema>;

/**
 * One owned instance. Consumed and retired instances never appear here: they
 * are history, not inventory. A reserved instance still appears, marked
 * locked, because the player still owns it.
 */
export const inventoryInstanceSchema = z.object({
  itemId: z.uuid(),
  definitionId: itemDefinitionIdSchema,
  category: z.enum(ITEM_CATEGORIES),
  rarity: raritySchema,
  level: z.number().int().min(1),
  /** Asset id in the generated manifest; null while the art does not exist. */
  assetKey: z.string().min(1).nullable(),
  tradable: z.boolean(),
  /**
   * Bound to this account by the provenance of its grant. Never tradable.
   *
   * Per INSTANCE, not per definition: the free starter `ship1` is bound, and a
   * `ship1` from a drop is not.
   */
  soulbound: z.boolean(),
  /** True while an active reservation blocks this instance. */
  locked: z.boolean(),
  /** Why it is locked; null when it is not. */
  lockedPurpose: z.enum(RESERVATION_PURPOSES).nullable(),
  /** True when this instance occupies a loadout slot right now. */
  equipped: z.boolean(),
  /** Which slot it occupies; null when it is not equipped. */
  equippedSlot: loadoutSlotSchema.nullable(),
  /** The account's ACTIVE pet. A third slot, with its own advice. */
  activePet: z.boolean(),
  /**
   * Corações Primordiais incorporados nesta cópia (gate V28F.A).
   *
   * `null` para tudo o que não é uma nave, e é uma distinção que importa: um
   * canhão não tem corações, e dizer `'0'` afirmaria uma propriedade que a
   * categoria não tem. Uma NAVE sem corações traz `'0'`, que é um facto.
   *
   * String decimal porque a contagem é `numeric(40, 0)` no banco e `bigint` no
   * servidor - a mesma regra dos saldos, e pela mesma razão: um `number` de JSON
   * perderia exactidão acima de 2^53.
   *
   * A contagem, nunca o bónus. O efeito é derivado dela por quem simula, e neste
   * gate ninguém o deriva.
   *
   * ADITIVO: `contractVersion` não se move, e todo o campo que um cliente antigo
   * lê continua lá e continua a significar o mesmo.
   */
  primordialHeartCount: decimalStringSchema.nullable(),
  /**
   * Whether the item could be listed or traded at this moment. Equipped and
   * reserved are DIFFERENT states - an equipped item is in use, not held for a
   * market operation - but both make an item unavailable, and the client only
   * needs the one answer to grey out a button.
   */
  availableForTrade: z.boolean(),
  /**
   * Why it is unavailable, in terms a player can be shown. Never an internal
   * detail: no reservation ids, no operation ids, no account ids.
   */
  unavailableReason: z.enum(TRADE_BLOCK_REASONS).nullable(),
});
export type InventoryInstanceWire = z.infer<typeof inventoryInstanceSchema>;

/**
 * One stack balance. Quantities are decimal strings - the vault shares the
 * ledger's exact 40-digit integer capacity and a JSON number would not.
 */
export const inventoryStackSchema = z.object({
  definitionId: itemDefinitionIdSchema,
  category: z.enum(ITEM_CATEGORIES),
  rarity: raritySchema,
  total: decimalStringSchema,
  reserved: decimalStringSchema,
  available: decimalStringSchema,
});
export type InventoryStackWire = z.infer<typeof inventoryStackSchema>;

export const inventoryResponseSchema = z.object({
  contractVersion: z.literal(INVENTORY_CONTRACT_VERSION),
  /**
   * The loadout version to send with an equip request. Optimistic concurrency
   * starts here: the client reads it with the list it is looking at.
   */
  loadoutVersion: z.number().int().min(0),
  /**
   * The skin worn right now, or null (ADR 0036).
   *
   * An opaque instance id and nothing else: no rarity, no art, no socket and no
   * bonus, because none of those exists yet and a field promising one would be
   * a promise the vault cannot keep.
   *
   * Redundant with `instances[].equippedSlot === 'skin'` and worth having
   * anyway: a client should not have to scan a paginated list to answer "what
   * am I wearing", and the worn skin may sit on a page it has not fetched.
   *
   * ADDITIVE, so `contractVersion` does not move: every field an older client
   * reads is still there and still means the same thing.
   */
  equippedSkinInstanceId: z.uuid().nullable(),
  /** Deterministic order: acquisition time, then id. */
  instances: z.array(inventoryInstanceSchema),
  /**
   * All of the account's stacks, on every page: the list is bounded by the
   * catalog, not by how much the player owns, so paginating it would only
   * complicate the client.
   */
  stacks: z.array(inventoryStackSchema),
  /** Cursor for the next page of instances; null on the last page. */
  nextCursor: inventoryCursorSchema.nullable(),
});
export type InventoryResponse = z.infer<typeof inventoryResponseSchema>;
